import type { MarketplaceRateTableTabName } from '@repo/supergraph/structuredContent/hydration/MarketplaceRateTable/constants';
import type { MarketplaceRateTableEntity } from '@repo/supergraph/structuredContent/hydration/MarketplaceRateTable/types';
import { formatLabel } from '@repo/utils/cdTerm/formatLabel';
import { formatCurrencyWithCentsIfNeeded } from '@repo/utils/currency/formatCurrency';

export type RateTableEntity = MarketplaceRateTableEntity;

export type RateTableDriver = {
  label: string;
  value: string;
  valueTooltip?: string;
};

type DriverGetter = (entity: RateTableEntity) => RateTableDriver;

// Labels and values mirror the original banking offer card (react-banking-offer-card). Currency
// values are formatted from the raw amounts rather than the supergraph drivers, whose values are
// pre-rounded to whole dollars (e.g. a $0.01 minimum balance would show as "$0").
const apy: DriverGetter = ({ driverApy }) => ({
  label: 'APY',
  value: driverApy?.value || 'N/A',
  valueTooltip: driverApy?.valueTooltip,
});

// Compliance: must read "Min. balance for APY", never "Min. balance" alone, and show $0 rather
// than N/A when there is no minimum.
const minimumBalance: DriverGetter = ({ minimumBalance: amount }) => ({
  label: 'Min. balance for APY',
  value: formatCurrencyWithCentsIfNeeded(amount ?? 0),
});

const monthlyFee: DriverGetter = ({ feeMonthly }) => ({
  label: 'Monthly fee',
  value: formatCurrencyWithCentsIfNeeded(feeMonthly ?? 0),
});

const cdTerm: DriverGetter = ({ cdDepositTerm }) => ({
  label: 'Term',
  value: formatLabel({ cdDepositTerm }) ?? 'N/A',
});

// The two columns shown on each product card, per tab.
export const DRIVERS_BY_TAB = {
  Savings: [apy, minimumBalance],
  'Cash Management': [apy, minimumBalance],
  CD: [apy, cdTerm],
  Checking: [apy, monthlyFee],
  'Money Market': [apy, minimumBalance],
} as const satisfies Record<MarketplaceRateTableTabName, readonly DriverGetter[]>;

type ValueFontSize = { xs: number; md: number };

const LARGE_VALUE: ValueFontSize = { xs: 24, md: 36 };
const MEDIUM_VALUE: ValueFontSize = { xs: 18, md: 28 };

// Driver value font size (mobile / desktop), per tab.
export const VALUE_FONT_SIZE_BY_TAB = {
  Savings: MEDIUM_VALUE,
  'Cash Management': MEDIUM_VALUE,
  CD: LARGE_VALUE,
  Checking: LARGE_VALUE,
  'Money Market': LARGE_VALUE,
} as const satisfies Record<MarketplaceRateTableTabName, ValueFontSize>;

export function getDrivers(tabName: MarketplaceRateTableTabName, entity: RateTableEntity) {
  return DRIVERS_BY_TAB[tabName].map(getDriver => getDriver(entity));
}
